import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { FlowchartFrame, VerticalChain, CourseNode, useCourseModal } from '../components/FlowchartParts';
import { CourseModal } from '../components/CourseModal';
import { Badge } from '../components/ui/badge';
import { ChevronRight, Wind, Layers, Wrench, GraduationCap, AlertTriangle } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

const OPEN_LINE_A = ['tdi-nitrox', 'tdi-advanced-nitrox', 'tdi-deco-procedures', 'tdi-extended-range'];
const OPEN_LINE_B = ['tdi-helitrox', 'tdi-trimix', 'tdi-advanced-trimix'];
const REBREATHER_LINE_A = ['tdi-air-diluent-ccr', 'tdi-air-diluent-deco-ccr', 'tdi-mixed-gas-ccr', 'tdi-advanced-mixed-gas-ccr'];
const REBREATHER_LINE_B = ['tdi-semi-closed-rebreather', 'tdi-helitrox-ccr'];
const OVERHEAD_LINE_A = ['tdi-cavern', 'tdi-intro-to-cave', 'tdi-full-cave'];
const OVERHEAD_EXTRAS = ['tdi-cavern-discovery', 'tdi-advanced-wreck', 'tdi-dpv', 'tdi-mine-diver', 'tdi-cave-survey', 'tdi-stage-cave', 'tdi-cave-dpv'];
const SERVICE_LINE_A = ['tdi-nitrox-blender', 'tdi-advanced-blender'];
const SERVICE_EXTRAS = ['tdi-o2-service-tech'];
const PRO_ORDER = ['tdi-non-diving-specialty-instructor', 'tdi-divemaster', 'tdi-instructor', 'tdi-instructor-trainer'];
const STANDALONE_OPEN = ['tdi-sidemount', 'tdi-intro-to-tech'];

export default function TDICourses() {
  const { settings } = useSettings();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const modal = useCourseModal();

  useEffect(() => {
    api.get('/courses?agency=TDI').then(({ data }) => setAll(data || [])).catch(() => setAll([])).finally(() => setLoading(false));
  }, []);

  const bySlug = useMemo(() => new Map(all.map((c) => [c.slug, c])), [all]);
  const list = (arr) => arr.map((s) => bySlug.get(s)).filter(Boolean);
  const bg = settings?.tdi_background_url || '/backgrounds/tdi-bg.jpg';

  return (
    <div className="pt-24 pb-20">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'brightness(1.3) saturate(1.15)' }} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/85 via-[#0a1628]/40 to-[#0a1628]/15" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0a1628]" />
          <div className="absolute inset-0 hero-radial" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24 relative">
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#00b4d8] font-semibold mb-3">
            <Link to="/" className="hover:text-white">Home</Link> <ChevronRight className="inline h-3 w-3 mx-1" /> Courses <ChevronRight className="inline h-3 w-3 mx-1" /> TDI
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight max-w-3xl">TDI Technical Diving Pathway</h1>
          <p className="mt-5 max-w-2xl text-white/75 text-base sm:text-lg leading-relaxed">
            TDI is the largest technical certification agency in the world. As one of the first to provide training in mixed-gas diving and rebreathers, TDI offers a wide selection of programs across Open Circuit, Rebreather, Overhead, Service and Professional disciplines. Click any node to view full course details.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Badge className="bg-white/6 text-white/75 border border-white/12 hover:bg-white/8"><Wind className="mr-1 h-3 w-3 text-[#00b4d8]" /> Open Circuit</Badge>
            <Badge className="bg-white/6 text-white/75 border border-white/12 hover:bg-white/8"><Layers className="mr-1 h-3 w-3 text-[#00b4d8]" /> Rebreather</Badge>
            <Badge className="bg-white/6 text-white/75 border border-white/12 hover:bg-white/8"><AlertTriangle className="mr-1 h-3 w-3 text-[#c8e600]" /> Overhead</Badge>
            <Badge className="bg-white/6 text-white/75 border border-white/12 hover:bg-white/8"><Wrench className="mr-1 h-3 w-3 text-[#00b4d8]" /> Service</Badge>
            <Badge className="bg-white/6 text-white/75 border border-white/12 hover:bg-white/8"><GraduationCap className="mr-1 h-3 w-3 text-[#c8e600]" /> Professional</Badge>
          </div>
        </div>
      </section>

      <section className="pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid lg:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-96 rounded-3xl bg-white/5 border border-white/10 animate-pulse" />
              ))}
            </div>
          ) : (
            <div data-testid="tdi-flowchart" className="grid lg:grid-cols-3 gap-6">
              <FlowchartFrame title="Open Circuit" subtitle="Stage cylinders and accelerated decompression">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#00b4d8] font-semibold mb-2">Nitrox / Deco line</div>
                <VerticalChain courses={list(OPEN_LINE_A)} onClick={modal.openCourse} variant="core" />
                <div className="my-5 border-t border-dashed border-white/10" />
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#00b4d8] font-semibold mb-2">Trimix line</div>
                <VerticalChain courses={list(OPEN_LINE_B)} onClick={modal.openCourse} variant="core" />
                <div className="my-5 border-t border-dashed border-white/10" />
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/55 font-semibold mb-2">Standalone</div>
                <div className="grid grid-cols-1 gap-3">
                  {list(STANDALONE_OPEN).map((c) => <CourseNode key={c.slug} course={c} onClick={modal.openCourse} />)}
                </div>
              </FlowchartFrame>

              <FlowchartFrame title="Rebreather" subtitle="Closed-circuit & semi-closed systems">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#00b4d8] font-semibold mb-2">CCR line</div>
                <VerticalChain courses={list(REBREATHER_LINE_A)} onClick={modal.openCourse} variant="core" />
                <div className="my-5 border-t border-dashed border-white/10" />
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#00b4d8] font-semibold mb-2">Alternative</div>
                <VerticalChain courses={list(REBREATHER_LINE_B)} onClick={modal.openCourse} />
              </FlowchartFrame>

              <FlowchartFrame title="Overhead Environment" subtitle="Caves, wrecks and tight overhead">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#c8e600] font-semibold mb-2">Cave line</div>
                <VerticalChain courses={list(OVERHEAD_LINE_A)} onClick={modal.openCourse} variant="core" />
                <div className="my-5 border-t border-dashed border-white/10" />
                <div className="text-[11px] uppercase tracking-[0.22em] text-white/55 font-semibold mb-2">Other overhead</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3">
                  {list(OVERHEAD_EXTRAS).map((c) => <CourseNode key={c.slug} course={c} onClick={modal.openCourse} />)}
                </div>
              </FlowchartFrame>

              <FlowchartFrame title="Service" subtitle="Blending & oxygen-clean technicians">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#00b4d8] font-semibold mb-2">Blender line</div>
                <VerticalChain courses={list(SERVICE_LINE_A)} onClick={modal.openCourse} variant="core" />
                <div className="my-5 border-t border-dashed border-white/10" />
                <div className="grid grid-cols-1 gap-3">
                  {list(SERVICE_EXTRAS).map((c) => <CourseNode key={c.slug} course={c} onClick={modal.openCourse} />)}
                </div>
              </FlowchartFrame>

              <FlowchartFrame title="Professional" subtitle="Become a TDI instructor">
                <VerticalChain courses={list(PRO_ORDER)} onClick={modal.openCourse} variant="professional" />
              </FlowchartFrame>

              <FlowchartFrame title="Safety & Prerequisites" subtitle="Read this before you book">
                <div className="text-sm text-white/70 leading-relaxed space-y-3">
                  <p>Technical diving operates outside the limits of recreational diving. Every TDI program has strict prerequisites — typically including a minimum number of logged dives, prior certifications and a written exam.</p>
                  <p>Our instructors will assess your readiness before you commit to a course. If you’re unsure where to begin, the <button onClick={() => { const c = list(['tdi-intro-to-tech'])[0]; if (c) modal.openCourse(c); }} className="text-[#00b4d8] hover:text-white">Intro to Tech</button> program is the perfect entry point.</p>
                  <p className="text-xs text-white/50">All TDI programs are taught to international TDI standards. Certifications are recognised worldwide.</p>
                </div>
              </FlowchartFrame>
            </div>
          )}
        </div>
      </section>

      <CourseModal open={modal.open} onOpenChange={modal.setOpen} course={modal.course} />
    </div>
  );
}
